import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  MapPin,
  Camera,
  Activity,
  AlertCircle,
  Users,
  PlayCircle,
  ChevronLeft,
  ChevronRight,
  X,
  Sparkles,
  ParkingCircle,
  Building2,
  Briefcase,
  UserCheck,
  Settings,
  Radio,
} from 'lucide-react';
import { SCREEN_CODES } from '../../constants/screenCodes';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useBranding } from '../../contexts/BrandingContext';
import Button from '../ui/Button';
import PlatformAdminLink from '../platform/PlatformAdminLink';
import { useAuth } from '../../contexts/AuthContext';

interface NavItem {
  name: string;
  path: string;
  icon: React.ReactNode;
  screenCode: string;
}

const navItems: NavItem[] = [
  { name: 'Campus Intelligence', path: '/education', icon: <UserCheck size={20} />, screenCode: SCREEN_CODES.PERSONS },
  {
    name: 'Dashboard',
    path: '/dashboard',
    icon: <LayoutDashboard size={20} />,
    screenCode: SCREEN_CODES.DASHBOARD,
  },
  {
    name: 'Locations',
    path: '/locations',
    icon: <MapPin size={20} />,
    screenCode: SCREEN_CODES.LOCATIONS,
  },
  {
    name: 'Cameras',
    path: '/cameras',
    icon: <Camera size={20} />,
    screenCode: SCREEN_CODES.CAMERAS,
  },
  {
    name: 'Live View',
    path: '/live',
    icon: <Activity size={20} />,
    screenCode: SCREEN_CODES.CAMERAS_LIVE,
  },
  {
    name: 'Events',
    path: '/events',
    icon: <AlertCircle size={20} />,
    screenCode: SCREEN_CODES.EVENTS,
  },
  { name: 'Sensors & Devices', path: '/sensors-devices', icon: <Radio size={20} />, screenCode: SCREEN_CODES.SENSORS_DEVICES },
  {
    name: 'Actions',
    path: '/actions',
    icon: <PlayCircle size={20} />,
    screenCode: SCREEN_CODES.ACTIONS,
  },
  {
    name: 'Entry/Exit',
    path: '/entry-exit',
    icon: <Users size={20} />,
    screenCode: SCREEN_CODES.ENTRY_EXIT,
  },
  {
    name: 'Person Registry',
    path: '/persons',
    icon: <UserCheck size={20} />,
    screenCode: SCREEN_CODES.PERSONS,
  },
  {
    name: 'Parking',
    path: '/parking',
    icon: <ParkingCircle size={20} />,
    screenCode: SCREEN_CODES.PARKING,
  },
  {
    name: 'Tenants',
    path: '/tenants',
    icon: <Building2 size={20} />,
    screenCode: SCREEN_CODES.TENANTS,
  },
  {
    name: 'Company',
    path: '/company',
    icon: <Briefcase size={20} />,
    screenCode: SCREEN_CODES.COMPANY,
  },
  {
    name: 'AI Search',
    path: '/ai-search',
    icon: <Sparkles size={20} />,
    screenCode: SCREEN_CODES.AI_SEARCH,
  },
  {
    name: 'Configuration',
    path: '/configuration',
    icon: <Settings size={20} />,
    screenCode: SCREEN_CODES.CONFIGURATION,
  },
];

interface SidebarProps {
  isOpen: boolean;
  onClose?: () => void;
  isCollapsed: boolean;
  onToggleCollapse?: () => void;
}

const navGroups = [
  { label: 'Overview', paths: ['/dashboard'] },
  { label: 'Monitoring', paths: ['/live', '/cameras', '/events', '/sensors-devices', '/actions', '/ai-search'] },
  { label: 'Management', paths: ['/education', '/locations', '/entry-exit', '/persons', '/parking'] },
  { label: 'Administration', paths: ['/tenants', '/company', '/configuration'] },
];

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, isCollapsed, onToggleCollapse }) => {
  const location = useLocation();
  const { canAccessScreen } = usePermissions();
  const { branding } = useBranding();
  const { user } = useAuth();

  React.useEffect(() => {
    if (!isOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [isOpen, onClose]);

  return (
    <>
      {isOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      <aside
        aria-label="Application sidebar"
        style={{ backgroundColor: 'var(--sidebar-bg)', borderRightColor: 'rgb(255 255 255 / 0.06)' }}
        className={`fixed inset-y-0 left-0 z-50 border-r transition-[width,transform] duration-200 ease-in-out grid grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden w-60 lg:translate-x-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } ${isCollapsed ? 'lg:w-[72px]' : 'lg:w-60'}`}
      >
        <div className={`h-16 shrink-0 flex items-center gap-3 px-5 ${isCollapsed ? 'lg:px-[18px]' : ''}`}>
          <Link to="/dashboard" onClick={onClose} aria-label={branding?.brand_name || 'SecureView'} className="flex min-w-0 items-center gap-2.5">
            <div className="w-9 h-9 flex-shrink-0 rounded-xl overflow-hidden bg-accent-primary flex items-center justify-center shadow-sm">
              {branding?.logo_url ? (
                <img src={branding.logo_url} alt="" className="w-full h-full bg-white object-contain" />
              ) : <Camera size={20} className="text-white" strokeWidth={1.8} />}
            </div>
            <div className={`min-w-0 ${isCollapsed ? 'lg:hidden' : ''}`}>
              <span style={{ color: 'var(--sidebar-text)' }} className="block truncate text-[17px] font-semibold tracking-tight">{branding?.brand_name || 'SecureView'}</span>
              <span style={{ color: 'var(--sidebar-text-muted)' }} className="block text-xs">Security workspace</span>
            </div>
          </Link>
          <Button type="button" variant="ghost" onClick={onClose} aria-label="Close sidebar" className="sidebar-control ml-auto lg:hidden">
            <X size={18} />
          </Button>
        </div>

        <nav aria-label="Main navigation" className={`min-h-0 overflow-y-auto px-3 pb-4 ${isCollapsed ? 'lg:px-2' : ''}`}>
          {navGroups.map(group => {
            const items = group.paths.map(path => navItems.find(item => item.path === path)!).filter(item => canAccessScreen(item.screenCode) &&
              (item.path !== '/education' || (user.companyId === 756)));
            if (!items.length) return null;
            return (
              <div key={group.label} className="mt-6 first:mt-3">
                <p style={{ color: 'var(--sidebar-text-muted)' }} className={`px-3 mb-2 text-xs font-medium uppercase tracking-[0.12em] ${isCollapsed ? 'lg:sr-only' : ''}`}>{group.label}</p>
                <div className="space-y-1">
                  {items.map(item => {
                    const active = location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
                    return (
                      <Link key={item.path} to={item.path} onClick={onClose} aria-label={item.name} aria-current={active ? 'page' : undefined}
                        title={isCollapsed ? item.name : undefined}
                        className={`sidebar-link flex items-center gap-3 h-[38px] px-3 rounded-lg text-[13px] font-medium transition-colors [&>svg]:h-[18px] [&>svg]:w-[18px] [&>svg]:shrink-0 ${isCollapsed ? 'lg:justify-center lg:px-0' : ''}`}>
                        {item.icon}
                        <span className={`truncate ${isCollapsed ? 'lg:hidden' : ''}`}>{item.name}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
          <PlatformAdminLink collapsed={isCollapsed} onClick={onClose} />
        </nav>

        {/* Keep controls in normal flow; only the navigation above may scroll. */}
        <div className="shrink-0 mx-3 border-t border-white/10 py-3">
          {onToggleCollapse && (
            <Button type="button" variant="ghost" fullWidth onClick={onToggleCollapse} aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-expanded={!isCollapsed}
              className="sidebar-control hidden lg:inline-flex">
              {isCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
              {!isCollapsed && <span>Collapse sidebar</span>}
            </Button>
          )}
          {branding?.footer_text && (
            <p style={{ color: 'var(--sidebar-text-muted)' }} className={`px-3 mt-2 text-xs ${isCollapsed ? 'lg:hidden' : ''}`}>{branding.footer_text}</p>
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
